import React, { ReactNode } from 'react'
import { connect, DispatchProp } from 'react-redux'
import { RouteComponentProps } from '../../../helpers/withRouter'
import Select from 'react-select'
import { AnyAction } from 'redux'
import { Application } from '../../../config'
import { Button, Page, Radiobutton, Spinner } from '../../../components'
import { images } from '../../../images'
import { AuthenticatedRoutable, IServiceInfo, ISuggestionComplaint, ISuggestionType } from '../../../models'
import literalsService from '../../../services/literals-service'
import { addSuggestionAction, fetchServicesAction } from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from './styles'
import { appsStyleSelected } from '../../../helpers/app'

const { isAmb } = Application

const AMB_COMPLAINTS_URL = 'https://solerisauret.com/queixes-i-reclamacions/'
const AMB_SUGGESTIONS_URL = 'https://solerisauret.com/suggeriments-i-agraiments/'

const AFFAIR_OPTIONS = Object.values(ISuggestionComplaint).filter(value => value !== ISuggestionComplaint.OBJECT_LOST)

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  serviceInfos: IServiceInfo[]
  isLoading: boolean
}

interface IState {
  type: ISuggestionType
  affair: string
  affairError: boolean
  serviceId: number | null
  description: string
  descriptionError: boolean
  anonymize: boolean
}

class ComplaintsAndSuggestions extends AuthenticatedRoutable<IProps, IState> {

  constructor(props: IProps) {
    super(props)
    this.state = {
      type: ISuggestionType.SUGGESTION,
      affair: '',
      affairError: false,
      serviceId: null,
      description: '',
      descriptionError: false,
      anonymize: false
    }
  }

  private get affairOptions() {
    return AFFAIR_OPTIONS
      .map(value => ({ value, label: literalsService.get(value.toLowerCase(), true) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  private get serviceOptions() {
    const { serviceInfos } = this.props
    return serviceInfos
      .map(service => ({ value: service.id, label: service.tag }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  public componentDidMount(): void {
    const { dispatch, serviceInfos } = this.props
    super.componentDidMount()
    if (serviceInfos.length === 0) dispatch(fetchServicesAction())
  }

  private get ambLinksContent(): ReactNode {
    return (
      <section className="amb-links">
        <div>
          <div className="amb-link-title">{ literalsService.get('complains', true) }</div>
          <span className="link" onClick={ () => window.open(AMB_COMPLAINTS_URL, '_blank') }>{ AMB_COMPLAINTS_URL }</span>
        </div>
        <div>
          <div className="amb-link-title">{ literalsService.get('suggestions', true) }</div>
          <span className="link" onClick={ () => window.open(AMB_SUGGESTIONS_URL, '_blank') }>{ AMB_SUGGESTIONS_URL }</span>
        </div>
      </section>
    )
  }

  private get formContent(): ReactNode {
    const { isLoading } = this.props
    const { type, description, descriptionError, anonymize } = this.state

    return (
      <section>
        <h3>{ literalsService.get('complaintsAndSuggestions', true) }</h3>
        <label className="fieldLabel">{ literalsService.get('type', true) }</label>
        <div className="radiobuttons-container">
          <div onClick={ () => this.setState({ type: ISuggestionType.SUGGESTION, affair: '' }) }>
            <Radiobutton
              selected={ type === ISuggestionType.SUGGESTION }
              label={ literalsService.get('suggestion', true) }
            />
          </div>
          <div onClick={ () => this.setState({ type: ISuggestionType.COMPLAINT, affair: '' }) }>
            <Radiobutton
              selected={ type === ISuggestionType.COMPLAINT }
              label={ literalsService.get('complaint', true) }
            />
          </div>
        </div>
        <label className="fieldLabel">{ literalsService.get('subject', true) }</label>
        <Select className="select" options={ this.affairOptions } placeholder={ literalsService.get('select', true) } onChange={ this.handleChangeAffair } />
        <label className="fieldLabel">{ literalsService.get('service', true) }</label>
        <Select className="select" options={ this.serviceOptions } placeholder={ literalsService.get('select', true) } onChange={ this.handleChangeService } />
        <label className="fieldLabel">{ literalsService.get('suggestionDescription', true) }</label>
        <textarea
          className={ descriptionError ? 'invalid' : '' }
          value={ description }
          onChange={ e => this.setState({ description: e.target.value }) }
          maxLength={ 250 }
        />
        <div className="anonymize-container" onClick={ () => this.setState({ anonymize: !anonymize }) }>
          <img src={ images[ anonymize ? 'checkBoxFilled' : 'checkBoxEmpty' ] } alt="" />
          <span>{ literalsService.get('sendAnonymously', true) }</span>
        </div>
        <Button
          title={ literalsService.get('send', true) }
          onPress={ this.handleSend }
          className="sendButton"
          filled
          upper
          isDisabled={ isLoading }
        />
      </section>
    )
  }

  public render(): ReactNode {
    const { isLoading } = this.props

    return (
      <Page>
        <Page className={ `authenticated ${appsStyleSelected?.style}` }>
          <StyledArticle>
            <img src={ images['goBack'] } className="backButton" onClick={ this.handleGoBack } alt="" />
            {
              isLoading ? (
                <article className="spinner-container">
                  <Spinner />
                </article>
              ) : isAmb ? this.ambLinksContent : this.formContent
            }
          </StyledArticle>
        </Page>
      </Page>
    )
  }

  private handleGoBack = (): void => {
    history.back()
  }

  private handleChangeAffair = (option: any) => {
    this.setState({ affair: option.value, affairError: false })
  }

  private handleChangeService = (option: any) => {
    this.setState({ serviceId: option.value })
  }

  private handleSend = () => {
    const { dispatch } = this.props
    const { type, affair, description, anonymize, serviceId } = this.state

    let valid = true
    if (!affair) { this.setState({ affairError: true }); valid = false }
    if (!description.trim().length) { this.setState({ descriptionError: true }); valid = false }
    if (!valid) return

    dispatch(addSuggestionAction(type, affair.toLowerCase(), affair as ISuggestionComplaint, description, anonymize, serviceId!))
  }
}

const mapStateToProps = ({ newReservation, complaintsAndSuggestions }: IStoreState) => ({
  serviceInfos: newReservation.serviceInfos,
  isLoading: complaintsAndSuggestions.isLoading
})

export default connect(mapStateToProps)(ComplaintsAndSuggestions)
